import React, { useState, useEffect, useRef } from 'react';
import eyeIcon from 'src/assets/icons/eye-icon.svg';
import eyeCloseIcon from 'src/assets/icons/eye-close-icon.svg';
import crossIcon from 'src/assets/icons/cross-icon.svg';
import { twMerge } from 'tailwind-merge';
import { id } from 'date-fns/locale';
import { getIn } from 'formik';

interface TextInputProps {
  type: string;
  variant: 'normal' | 'line';
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  isDisabled?: boolean;
  placeHolder?: string;
  htmlLabelFor?: string;
  helperMessage?: string;
  label?: string;
  value?: string | number;
  validState?: 'valid' | 'inValid' | undefined;
  className?: string;
  inputFieldClassName?: string;
  required?: boolean;
  name?: string;
  view?: boolean;
  isClear?: boolean;
  leftIcon?: { path: string; iconAlt: string };
  rightIcon?: { path: string; iconAlt: string };
  leftText?: string;
  rightText?: string;
  rightTextClassName?: string;
  innerRef?: any;
  isReadOnly?: boolean;
  pattern?: string;
  onKeyDown?: (event: any) => void;
  autoComplete?: string;
  handleError?: any;
  handleTouched?: any;
  leftIconClassName?: string;
}

function TextInput({
  variant,
  onChange,
  onClear,
  htmlLabelFor,
  type,
  isDisabled,
  placeHolder,
  helperMessage,
  label,
  validState,
  value,
  className,
  required,
  onBlur,
  name = '',
  view,
  leftIcon,
  inputFieldClassName,
  isClear = true,
  leftText,
  rightText,
  rightIcon,
  innerRef,
  rightTextClassName,
  isReadOnly,
  pattern,
  autoComplete,
  onKeyDown,
  handleError,
  handleTouched,
  leftIconClassName,
}: TextInputProps) {
  const inputDivRef = useRef<any>(null);
  const [isPasswordShow, setIsPasswordShow] = useState(false);
  const [showClearBtn, setShowClearBtn] = useState(false);
  const inputReff = useRef<any>(null);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (inputDivRef.current && !inputDivRef.current.contains(event.target)) {
        onFocus(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [inputDivRef]);

  let inputVariantClasses = {
    normal: 'bg-white rounded-full font-SFProDisplay',
    line: 'border-t-0 border-r-0 border-l-0 border-b-1 focus:ring-gray-500 focus:ring-0 focus:border-gray-500 hover:border-gray-500 caret-[#f15a29]',
  };
  let inputClasses: any = {
    valid:
      'border border-zinc-200 focus:ring-lightBlue focus:border-transparent',
    inValid: 'border-rose-500 focus:ring-lightBlue focus:border-transparent',
    normal:
      'border border-zinc-200 ring-0 focus:ring-lightBlue focus:border-transparent hover:border-borderBlue',
  };
  let lineInputClasses: any = {
    valid: 'border-green-500 focus:border-gray-500 focus:outline-0',
    inValid: 'border-rose-500 focus:border-gray-500 focus:outline-0',
    normal: 'border-gray-300 ',
  };
  let helperMessageClasses: any = {
    valid: 'text-green-500',
    inValid: 'text-rose-500',
  };

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      !(event.target.value === ' ' && event.target.value.length) &&
      onChange
    ) {
      onChange(event);
    }
  };

  const onClearValue = () => {
    setShowClearBtn(false);
    onClear && onClear();
  };

  const onFocus = (isFocused: boolean) => {
    if (isReadOnly) return;
    setShowClearBtn(isFocused);
  };

  const handleWheel = (e: any) => {
    type === 'number' && inputReff?.current.blur();
  };

  const FormikError = (errors: any, touched: any, fieldName: any) => {
    const error = getIn(errors, fieldName);
    const touch = getIn(touched, fieldName);
    return touch && error ? error : null;
  };

  const errorMessage = FormikError(handleError, handleTouched, name);

  return (
    <div className={`mb-2 ${className ? className : ''}`}>
      {label && (
        <label
          htmlFor={htmlLabelFor}
          className="form-label block mb-3 text-blue-950 text-sm font-medium font-SFProDisplay leading-none"
        >
          {label}
          {required && <span className="pl-1 text-red-600">*</span>}
        </label>
      )}
      <div className="flex gap-1 items-center w-full bg-[#f3f4f6] rounded-full">
        <div className="relative w-full" ref={inputDivRef}>
          {leftIcon && (
            <img
              src={leftIcon?.path}
              alt={leftIcon?.iconAlt}
              className={`absolute top-[13px] left-3 ${leftIconClassName}`}
            />
          )}
          {leftText ? (
            <p
              className={`absolute left-3 z-10 top-[11px] font-SFProDisplay text-[#231F20CC]`}
            >
              {leftText}
            </p>
          ) : null}
          <input
            ref={innerRef || inputReff}
            type={type === 'password' && isPasswordShow ? 'text' : type}
            className={twMerge(
              `block font-SFProDisplay text-blue-950 text-sm font-medium w-full h-12 p-4 pr-7 pt-[5px] pb-2  placeholder:font-normal  placeholder:text-sm placeholder:text-slate-500 justify-start items-center gap-2.5  ${
                view ? '' : 'disabled:text-gray-300'
              } disabled:cursor-not-allowed disabled:border-zinc-200 disabled:hover:border-zinc-200 disabled:bg-slate-200 disabled:bg-opacity-50 disabled:font-normal disabled:text-[#0B1B4E]  disabled:text-opacity-40 disabled:placeholder:text-opacity-40 hover:border-borderBlue 
              ${inputVariantClasses[variant]} 
              ${
                variant === 'line'
                  ? lineInputClasses[validState ? validState : 'normal']
                  : inputClasses[
                      validState && !isDisabled ? validState : 'normal'
                    ]
              }
              ${errorMessage && inputClasses['inValid']}
              ${leftIcon ? ' pl-10' : ''}`,
              inputFieldClassName ?? inputFieldClassName
            )}
            id={htmlLabelFor}
            name={name}
            placeholder={placeHolder}
            disabled={isDisabled || view}
            value={value}
            onChange={onChangeHandler}
            onFocus={() => {
              onFocus(true);
            }}
            onBlur={onBlur}
            readOnly={isReadOnly}
            pattern={pattern}
            onKeyDown={onKeyDown}
            onWheel={handleWheel}
            autoComplete={autoComplete}
          />
          {rightText ? (
            <p
              className={`absolute right-8 top-[11px] font-SFProDisplay text-[#231F20CC] ${rightTextClassName}`}
            >
              {rightText}
            </p>
          ) : null}
          {showClearBtn && isClear && type !== 'password' && (
            <div className="absolute h-full right-3 top-[18px] bg-transparent">
              <img src={crossIcon} alt="" onClick={onClearValue} />
            </div>
          )}
          {type === 'password' && (
            <div
              className={`absolute h-full right-4 top-[12px] bg-transparent${
                isDisabled ? ' cursor-not-allowed' : ''
              }`}
            >
              <div
                onClick={() => {
                  setIsPasswordShow(!isPasswordShow);
                }}
                className={`${isDisabled && 'pointer-events-none'}`}
              >
                <img
                  className="h-6 w-6"
                  src={isPasswordShow ? eyeCloseIcon : eyeIcon}
                  alt={isPasswordShow ? 'show password' : 'hide password'}
                />
              </div>
            </div>
          )}
        </div>
        {rightIcon && (
          <img
            src={rightIcon?.path}
            alt={rightIcon?.iconAlt}
            className="mx-3"
          />
        )}
      </div>
      {helperMessage && validState === 'inValid' && (
        <span
          className={`block mt-2 font-SFProDisplay text-xs ${
            validState && !isDisabled
              ? helperMessageClasses[validState]
              : 'text-slate-500'
          }`}
        >
          {helperMessage}
        </span>
      )}
      <span className="block mt-2 font-SFProDisplay text-xs text-rose-500">
        {errorMessage}
      </span>
    </div>
  );
}

export default TextInput;
